import * as React from "react";
import {
  deleteTranslation,
  readTranslations,
  saveTranslations,
  type AdminLocale,
} from "@/api/admin";
import { useAction, useAsync } from "@/lib/useAsync";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, Input, Select, Textarea } from "@/components/ui/input";
import { ConfirmModal, Modal } from "@/components/ui/modal";
import { Notice, Warnings } from "@/components/ui/feedback";
import { SkeletonRows } from "@/components/ui/skeleton";
import { TBody, TD, TEmpty, TH, THead, TR, TableShell } from "@/components/ui/table";

import { isNamespace, type Namespace } from "@ewn/shared";
import { TranslationTransfer } from "./TranslationTransfer";

/**
 * Every string the shop renders, in one language, beside another for reference.
 *
 * ONLY THE CHANGED KEYS ARE SENT. `PUT /admin/i18n/:locale/:namespace` is an
 * UPSERT of whatever it is given, so what this chooses to send IS what gets
 * written -- and sending the whole map back on every save would quietly
 * overwrite a colleague's edit to a key this operator never touched.
 *
 * THE REFERENCE COLUMN IS AN EDITING AID, NOT A FALLBACK. The storefront builds
 * one locale at a time and has no runtime fallback: a key missing from a
 * language is missing, and that is what the build fails on. Showing another
 * language's words beside the box is how a translator knows what the box is
 * for, and nothing more -- which is why a missing row says "untranslated"
 * rather than quietly rendering the reference as though it were the answer.
 */

export function StringsEditor({
  locales,
  locale,
  onLocale,
  onSaved,
}: {
  locales: AdminLocale[];
  locale: string;
  onLocale: (code: string) => void;
  onSaved: () => void;
}) {
  const [namespace, setNamespace] = React.useState<Namespace>("site");
  const [reference, setReference] = React.useState<string>("");
  const [filter, setFilter] = React.useState("");
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});
  const [adding, setAdding] = React.useState(false);
  const [removing, setRemoving] = React.useState<string | null>(null);
  const action = useAction();

  const referenceCode = reference || locales[0]?.code || locale;

  const { data, loading, error, reload } = useAsync(async () => {
    const [target, base] = await Promise.all([
      readTranslations(locale, namespace),
      // The reference is a second read of the same endpoint, never a merge:
      // two maps, kept apart, so nothing can render one in place of the other.
      referenceCode === locale
        ? Promise.resolve(null)
        : readTranslations(referenceCode, namespace),
    ]);
    return { strings: target.strings, base: base?.strings ?? null };
  }, [locale, namespace, referenceCode]);
  // What a language file is made from: the saved strings, and only once the
  // last read has landed. `useAsync` keeps the previous answer on screen while
  // a read runs -- another language's, or this one's from before a save --
  // and after one fails, and a file of that would be a file of the wrong words.
  // The editor below goes on showing that answer: a table blanked on every
  // read loses the row being read.
  const saved = data && !loading && !error ? data.strings : null;

  // Anything typed belongs to the locale and namespace it was typed against.
  React.useEffect(() => {
    setDrafts({});
  }, [locale, namespace]);

  const strings = data?.strings ?? {};
  const base = data?.base ?? null;

  const keys = React.useMemo(() => {
    const all = new Set([...Object.keys(strings), ...Object.keys(base ?? {})]);
    const needle = filter.trim().toLowerCase();
    return [...all]
      .filter((key) => {
        if (!needle) return true;
        return (
          key.toLowerCase().includes(needle) ||
          (strings[key] ?? "").toLowerCase().includes(needle) ||
          (base?.[key] ?? "").toLowerCase().includes(needle)
        );
      })
      .sort();
  }, [strings, base, filter]);

  const dirty = Object.keys(drafts).filter((key) => drafts[key] !== (strings[key] ?? ""));

  async function save() {
    const entries: Record<string, string> = {};
    for (const key of dirty) entries[key] = drafts[key]!;
    if (await action.run(() => saveTranslations(locale, namespace, entries))) {
      setDrafts({});
      reload();
      // The language list carries the per-namespace counts, and a new key
      // changes them.
      onSaved();
    }
  }

  return (
    <Card>
      {/* EVERY CONTROL IN HERE CARRIES ITS OWN WIDTH, and that is not
          decoration. `fieldBase` is `w-full`, which inside a flex row resolves
          to the row's whole width -- so the first screenshot of this screen had
          four controls stacked vertically down the right-hand side, each one
          the width of the card, with the word "Strings" stranded beside them.
          A toolbar is a row; a row needs widths. */}
      <CardHeader className="flex-wrap gap-2">
        <CardTitle>Strings</CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            size="sm"
            className="w-44"
            aria-label="Language being edited"
            value={locale}
            onChange={(e) => onLocale(e.target.value)}
          >
            {locales.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name} ({l.code})
              </option>
            ))}
          </Select>
          <Select
            size="sm"
            className="w-32"
            aria-label="Which strings"
            value={namespace}
            onChange={(e) => {
              // Narrowed rather than cast: the two options below are the only
              // values this select renders, and a third added to `Namespace`
              // without an option here would be silently accepted by a cast.
              if (isNamespace(e.target.value)) setNamespace(e.target.value);
            }}
          >
            <option value="site">Website</option>
            <option value="email">Emails</option>
          </Select>
          <Select
            size="sm"
            // w-52, not w-44: at 44 the option text rendered as "Compare with
            // er" -- a label cut mid-word is a label that has to be guessed at.
            className="w-52"
            aria-label="Reference language"
            value={referenceCode}
            onChange={(e) => setReference(e.target.value)}
          >
            {locales.map((l) => (
              <option key={l.code} value={l.code}>
                Compare with {l.code}
              </option>
            ))}
          </Select>
          <Input
            size="sm"
            className="w-48"
            aria-label="Filter strings"
            placeholder="Filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {error && <Notice tone="error">{error}</Notice>}
        {action.error && <Notice tone="error">{action.error}</Notice>}
        <Warnings warnings={action.warnings} />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            {keys.length} {keys.length === 1 ? "string" : "strings"}
            {dirty.length > 0 && ` · ${dirty.length} changed`}
          </div>
          <div className="flex gap-2">
            <TranslationTransfer
              // A new language or set starts over: an open preview was planned
              // against the strings it is leaving.
              key={`${locale}|${namespace}`}
              locale={locale}
              namespace={namespace}
              strings={saved}
              busy={action.busy || dirty.length > 0}
              onImported={() => {
                reload();
                onSaved();
              }}
            />
            <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
              Add a string
            </Button>
            <Button size="sm" disabled={action.busy || dirty.length === 0} onClick={() => void save()}>
              {action.busy ? "Saving…" : `Save ${dirty.length || ""}`.trim()}
            </Button>
          </div>
        </div>

        {loading && keys.length === 0 ? (
          <SkeletonRows rows={6} cols={3} />
        ) : (
          <TableShell className="-mx-4">
            <THead>
              <TH className="w-64">Key</TH>
              {base && <TH className="w-1/3">{referenceCode}</TH>}
              <TH>{locale}</TH>
              <TH />
            </THead>
            <TBody>
              {keys.length === 0 && (
                <TEmpty colSpan={base ? 4 : 3}>
                  {filter ? "Nothing matches that." : "No strings in this namespace yet."}
                </TEmpty>
              )}
              {keys.map((key) => {
                const stored = strings[key];
                const value = drafts[key] ?? stored ?? "";
                return (
                  <TR key={key}>
                    <TD className="align-top font-mono text-xs break-all">
                      {key}
                      {stored === undefined && (
                        <Badge color="amber" className="ml-2">
                          untranslated
                        </Badge>
                      )}
                    </TD>
                    {base && (
                      <TD className="align-top text-xs text-muted-foreground">{base[key] ?? "—"}</TD>
                    )}
                    <TD className="align-top">
                      <Textarea
                        rows={value.length > 90 ? 3 : 1}
                        aria-label={`${key} in ${locale}`}
                        value={value}
                        onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                      />
                    </TD>
                    <TD className="align-top">
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={stored === undefined}
                        title={stored === undefined ? "There is nothing stored to remove" : undefined}
                        onClick={() => setRemoving(key)}
                      >
                        Remove
                      </Button>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </TableShell>
        )}
      </CardContent>

      <AddStringModal
        open={adding}
        busy={action.busy}
        locale={locale}
        onClose={() => setAdding(false)}
        onAdd={async (key, value) => {
          if (await action.run(() => saveTranslations(locale, namespace, { [key]: value }))) {
            setAdding(false);
            reload();
            onSaved();
          }
        }}
      />

      <ConfirmModal
        open={removing !== null}
        title="Remove this string?"
        confirmLabel="Remove it"
        busy={action.busy}
        onClose={() => setRemoving(null)}
        body={
          <>
            <p>
              <span className="font-mono text-xs">{removing}</span> disappears from{" "}
              <span className="font-medium">{locale}</span> only.
            </p>
            <p className="mt-2">
              If the site renders it, the next build in this language will fail rather than show an
              empty space.
            </p>
          </>
        }
        onConfirm={() => {
          if (!removing) return;
          void action
            .run(() => deleteTranslation(locale, namespace, removing))
            .then((ok) => {
              setRemoving(null);
              if (ok) {
                reload();
                onSaved();
              }
            });
        }}
      />
    </Card>
  );
}

function AddStringModal({
  open,
  busy,
  locale,
  onClose,
  onAdd,
}: {
  open: boolean;
  busy: boolean;
  locale: string;
  onClose: () => void;
  onAdd: (key: string, value: string) => void;
}) {
  const [key, setKey] = React.useState("");
  const [value, setValue] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setKey("");
      setValue("");
    }
  }, [open]);

  return (
    <Modal
      open={open}
      title={`Add a string to ${locale}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button disabled={busy || key.trim().length === 0} onClick={() => onAdd(key.trim(), value)}>
            {busy ? "Saving…" : "Add it"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label="Key"
          htmlFor="add-key"
          // The rule itself is the worker's, and it is not restated here: a
          // second copy of a regex on this side is a copy that goes out of
          // date without anything failing. What comes back is its sentence.
          hint="The name the site renders this by, like checkout.pay_now."
        >
          <Input
            id="add-key"
            className="font-mono text-xs"
            value={key}
            onChange={(e) => setKey(e.target.value)}
          />
        </Field>
        <Field label="Text" htmlFor="add-value">
          <Textarea id="add-value" value={value} onChange={(e) => setValue(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}
